// Audit log viewer (ADMIN / SUPER_ADMIN) — every create/update/delete with before/after.
import { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { useList } from "@/hooks/use-list";
import { PageHeader } from "@/components/page-header";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { PaginationBar } from "@/components/pagination-bar";
import { useAuthStore } from "@/stores/auth-store";
import { ChevronDown, ChevronRight } from "lucide-react";

const fmtDateTime = (d?: string | null) => (d ? new Date(d).toLocaleString() : "—");
const actionVariant = (a: string): "default" | "secondary" | "destructive" => (a === "DELETE" ? "destructive" : a === "CREATE" ? "default" : "secondary");

export default function AuditLogPage() {
  const me = useAuthStore((s) => s.me);
  const isAdmin = me?.role === "ADMIN" || me?.role === "SUPER_ADMIN";
  const [q, setQ] = useState("");
  const [entity, setEntity] = useState("");
  const [action, setAction] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data, isLoading } = useList<any>("/audit-logs", { q, entity, action, page, pageSize });
  const rows = data?.data ?? [];
  const total = data?.pagination?.total;

  if (!isAdmin) {
    return (
      <>
        <PageHeader title="Audit Log" />
        <div className="p-6 text-sm text-muted-foreground">You don't have permission to view this page.</div>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Audit Log" description={<Link to="/admin" className="hover:underline">← Back to Admin</Link>} />
      <div className="space-y-4 p-6">
        <div className="flex flex-wrap items-center gap-2">
          <Input placeholder="Search entity / record id…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="w-64" />
          <Input placeholder="Entity (e.g. Deal)" value={entity} onChange={(e) => { setEntity(e.target.value); setPage(1); }} className="w-44" />
          <Select value={action} onChange={(e) => { setAction(e.target.value); setPage(1); }} className="w-36">
            <option value="">All actions</option>
            <option value="CREATE">Create</option>
            <option value="UPDATE">Update</option>
            <option value="DELETE">Delete</option>
          </Select>
        </div>
        <div className="rounded-md border">
          <Table>
            <Thead>
              <Tr><Th className="w-8"></Th><Th>Time</Th><Th>User</Th><Th>Action</Th><Th>Entity</Th><Th>Record</Th></Tr>
            </Thead>
            <Tbody>
              {isLoading && <Tr><Td colSpan={6} className="py-8 text-center text-muted-foreground">Loading…</Td></Tr>}
              {!isLoading && rows.length === 0 && <Tr><Td colSpan={6} className="py-8 text-center text-muted-foreground">No audit entries.</Td></Tr>}
              {rows.map((r: any) => (
                <Fragment key={r.id}>
                  <Tr className="group">
                    <Td><button onClick={() => setExpanded(expanded === r.id ? null : r.id)} aria-label="Toggle details">{expanded === r.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</button></Td>
                    <Td className="whitespace-nowrap text-sm">{fmtDateTime(r.createdAt)}</Td>
                    <Td className="text-sm">{r.user?.name ?? "—"}</Td>
                    <Td><Badge variant={actionVariant(r.action)}>{r.action}</Badge></Td>
                    <Td>{r.entity}</Td>
                    <Td className="font-mono text-xs text-muted-foreground">{r.entityId}</Td>
                  </Tr>
                  {expanded === r.id && (
                    <Tr>
                      <Td colSpan={6} className="bg-muted/30">
                        <div className="grid gap-3 p-2 md:grid-cols-2">
                          <div>
                            <div className="mb-1 text-xs font-medium text-muted-foreground">Before</div>
                            <pre className="max-h-60 overflow-auto rounded bg-card p-2 text-xs">{r.before ? JSON.stringify(r.before, null, 2) : "—"}</pre>
                          </div>
                          <div>
                            <div className="mb-1 text-xs font-medium text-muted-foreground">After</div>
                            <pre className="max-h-60 overflow-auto rounded bg-card p-2 text-xs">{r.after ? JSON.stringify(r.after, null, 2) : "—"}</pre>
                          </div>
                        </div>
                        {(r.ipAddress || r.userAgent) && <div className="px-2 pb-2 text-xs text-muted-foreground">{[r.ipAddress, r.userAgent].filter(Boolean).join(" · ")}</div>}
                      </Td>
                    </Tr>
                  )}
                </Fragment>
              ))}
            </Tbody>
          </Table>
        </div>
        <PaginationBar page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(1); }} />
      </div>
    </>
  );
}
